import { useEffect, useState } from "react";
import { useLocation } from "wouter";

import { DraftCard } from "./ui";

import { useGetDrafts, useDeleteCargoOrder, useUpDraft } from "@/common/api";
import { Card, CardContent, Button, TablePagination } from "@/common/components";
import { ROUTES } from "@/common/config";
import { useAuth, useToast } from "@/common/hooks";
import { pluralize } from "@/common/lib";

export const DraftsPage = () => {
  const [, setLocation] = useLocation();

  const {
    auth: { id },
  } = useAuth();
  const { toast } = useToast();

  const { mutateAsync: mutateDeleteAsync } = useDeleteCargoOrder();
  const { mutateAsync: mutateUpDraftAsync } = useUpDraft();

  const [page, setPage] = useState(1);
  const LIMIT = 10;

  const dataDraftsQuery = useGetDrafts({
    id: id!,
    page: page,
    limit: LIMIT,
  });
  const dataDrafts = dataDraftsQuery?.data?.data.payload;
  const totalPages = dataDrafts?.totalPage;
  const totalItems = dataDrafts?.totalItems;

  const handleDeleteOrder = (orderId: string) => {
    mutateDeleteAsync({
      params: { id: orderId! },
    })
      .then(() => dataDraftsQuery.refetch())
      .then(() => {
        toast({
          title: "Заявка удалена",
          description: "Заявка успешно удалена",
        });
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка удаления заявки",
          description:
            error instanceof Error ? error.message : "Произошла ошибка при удалении заявки",
        });
      });
  };
  const handleUpDraft = (orderId: string) => {
    mutateUpDraftAsync({
      params: { id: orderId! },
    })
      .then(() => dataDraftsQuery.refetch())
      .then(() => {
        toast({
          title: "Заявка отправлена",
          description: "Заявка успешно отправлена на модерацию",
        });
      })
      .catch((error: unknown) => {
        toast({
          variant: "destructive",
          title: "Ошибка отправки заявки",
          description:
            error instanceof Error
              ? error.message
              : "Произошла ошибка при отправке заявки на модерацию",
        });
      });
  };

  useEffect(() => {
    dataDraftsQuery.refetch();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start md:items-center flex-col md:flex-row">
        <h1 className="text-3xl font-bold text-text-heading">Черновики</h1>
        <p className="text-muted-foreground">
          Найдено: {pluralize(totalItems, "черновик", "черновика", "черновиков")}
        </p>
      </div>

      {totalItems === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="text-center">
              <h3 className="text-lg font-medium text-text-heading mb-2">
                Нет сохраненных черновиков
              </h3>
              <p className="text-muted-foreground mb-4">
                Создайте новую заявку и сохраните её как черновик
              </p>
              <Button onClick={() => setLocation(ROUTES.CREATE_ORDER)}>Создать заявку</Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {dataDrafts?.items?.map((request) => (
            <DraftCard
              key={request.id}
              data={request}
              onDelete={handleDeleteOrder}
              onSubmit={handleUpDraft}
            />
          ))}
          <div className="mt-6">
            <TablePagination
              currentPage={page}
              totalPages={totalPages ?? 0}
              totalItems={totalItems ?? 0}
              pageSize={LIMIT}
              onPageChange={(page) => {
                setPage(page);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};
